import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { PurchaseOrder } from 'src/models/purchase-order.model';
import { RodeoProductGroup } from 'src/models/rodeo-product-config';
import { StaffSearchOptions } from 'src/models/staff-search-options.model';
import { StripePayout } from 'src/models/Stripe/stripe-payout';
import { PurchaseOrderRefund } from 'src/models/purchase-order-refund.model';
import { FormBuilder } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  constructor(
    private api: ApiService,
    private fb: FormBuilder
  ) {}

  async createPaymentIntent(request: any) {
    return this.api.post(`${this.api.endpoints.payment}/PaymentSheet`, request);
  }

  async getPaymentDisplayInfo(request: any) {
    return this.api.post(
      `${this.api.endpoints.payment}/GetPaymentDisplayInfo`,
      request,
    );
  }

  async savePurchaseOrder(order: PurchaseOrder, saveChanges: boolean = true) {
    return this.api.post<PurchaseOrder>(`${this.api.endpoints.payment}/SavePurchaseOrder?saveChanges=${saveChanges}`, order);
  }

  //used for membership orders that do not require payment
  async createAndProcessOrder(request: any, markAsPaid: boolean) {
    return this.api.post(
      `${this.api.endpoints.payment}/CreateAndProcessOrder?markAsPaid=${markAsPaid ? 'true' : 'false'}`,
      request,
    );
  }

  async getCustomerPaymentMethods(accountId: number, useSandbox: boolean) {
    return this.api.post<any[]>(
      `${this.api.endpoints.payment}/GetCustomerPaymentMethods`,
      { accountId, useSandbox },
    );
  }

  async deletePaymentMethod(accountId: number, paymentMethodId: string, useSandbox: boolean) {
    return this.api.post(
      `${this.api.endpoints.payment}/DeleteCustomerStripePaymentMethod`,
      {accountId, paymentMethodId, useSandbox },
    );
  }

  async savePaymentMethod(paymentMethodId: string, accountId: number, customerId: string, useSandbox: boolean) {
    return this.api.post(`${this.api.endpoints.payment}/SaveCustomerPaymentMethod`, {paymentMethodId, accountId, customerId, useSandbox});
  }

  async getRodeoAddons(rodeoId: number) {
    return this.api.get<RodeoProductGroup[]>(`${this.api.endpoints.payment}/GetRodeoAdditionalProducts?rodeoId=${rodeoId}&showAllProducts=true`);
  }

  public async getStripePayoutsForSecretary(options: StaffSearchOptions, isTest = false) {
    return this.api.post<StripePayout[]>(`${this.api.endpoints.payment}/GetStripePayoutsForSecretary?isTest=${isTest}`, options);
  }

  public async getStripeTransactionsForPayout(secretaryId: number, payoutId: string, isTest = false) {
    return this.api.get<any>(`${this.api.endpoints.payment}/GetStripeTransactionsForPayout?secretaryId=${secretaryId}&payoutId=${payoutId}&isTest=${isTest}`)
  }

  public async refundOrder(request: PurchaseOrderRefund) {
    return this.api.post(`${this.api.endpoints.payment}/RefundOrder`, request);
  }

  public async generatePaymentPlanPayments(request: {
    numberOfInstallments: number, 
    paymentPlanInterval: number, 
    splitPaymentWithAccountIds: number[], 
    orderId: number, 
    order: PurchaseOrder 
  }) {
    return this.api.post<PurchaseOrder>(`${this.api.endpoints.payment}/GeneratePaymentPlanPayments`, request);
  }

  public generateOrderForm(order: PurchaseOrder) {
    return this.fb.group({
      id: order?.id || 0,
      accountId: order?.accountId,
      receiptEmail: order?.receiptEmail,
      stripeCustomerId: order?.stripeCustomerId,
      stripePaymentMethodId: order?.stripePaymentMethodId,
      paymentMethod: order?.paymentMethod,
      numOfInstallments: order?.numOfInstallments,
      initalDeposit: order?.initalDeposit,
      amountRemaining: order?.amountRemaining,
      subtotal: order?.subtotal,
      feesTotal: order?.feesTotal,
      totalAmountCollectedByNextGen: order?.totalAmountCollectedByNextGen,
      totalStripeTransferToOtherAccounts: order?.totalStripeTransferToOtherAccounts,
      totalCharged: order?.totalCharged,
      rodeoId: order?.rodeoId,
      rodeoName: order?.rodeoName,
      secretaryId: order?.secretaryId,
      completedOn: order?.completedOn,
      createdOn: order?.createdOn,
      canceledOn: order?.canceledOn,
      isCanceled: order?.isCanceled,
      isTest: order?.isTest,
      amountPaid: order?.amountPaid,
      amountRefunded: order?.amountRefunded,
      amountComped: order?.amountComped,
      autoPayEnabled: order?.autoPayEnabled,
      splitPaymentsByCustomer: order?.splitPaymentsByCustomer,

      lineItems: this.fb.array([]),
      payments: this.fb.array([]),
      refunds: this.fb.array([]),
      stripeTransfers: this.fb.array(order?.stripeTransfers.map(x => {
        return this.fb.group({
          id: x.id,
          purchaseOrderPaymentId: x.purchaseOrderPaymentId,
          purchaseOrderLineItemId: x.purchaseOrderLineItemId,
          stripeConnectAccount: x.stripeConnectAccount,
          secretaryName: x.secretaryName,
          secretaryId: x.secretaryId,
          amountToTransferCents: x.amountToTransferCents,
          amountReversedCents: x.amountReversedCents,
          isProcessed: x.isProcessed,
          stripeTransferGroup: x.stripeTransferGroup,
          stripeTransferId: x.stripeTransferId,
          createdOn: x.createdOn,
          completedOn: x.completedOn,
          reversedOn: x.reversedOn,
        })
      }) || [])
    });
  }

  public async getSetupIntentKeys(accountId: number, isTest: boolean) {
    return this.api.post<{
      paymentIntentClientSecret: string;
      customerEphemeralKeySecret: string;
      customerId: string;
    }>(
      `${this.api.endpoints.payment}/GetCustomerPaymentMethodSetupIntent?accountId=${accountId}&isTest=${isTest}`,
      null
    );
  }
}
